import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause, CaretRight, CaretLeft, X, Path, BookOpen, SpeakerHigh, SpeakerSlash, MicrophoneStage, Check } from "@phosphor-icons/react";
import TopNav from "@/components/TopNav";
import SolarSystem from "@/components/SolarSystem";
import GalaxyMap from "@/components/GalaxyMap";
import DeepDive from "@/components/DeepDive";
import api from "@/lib/api";

const noop = () => {};
const VOICE_KEY = "stellarnav_voice";

export default function DemoMode() {
  const [journeys, setJourneys] = useState([]);
  const [objects, setObjects] = useState([]);
  const [active, setActive] = useState(null);
  const [stopIndex, setStopIndex] = useState(0);
  const [focusToken, setFocusToken] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [deepDiveObj, setDeepDiveObj] = useState(null);

  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState(() => localStorage.getItem(VOICE_KEY) || "naturalist");
  const [muted, setMuted] = useState(false);
  const [voiceMenu, setVoiceMenu] = useState(false);
  const [narrating, setNarrating] = useState(false);

  const audioRef = useRef(null);
  if (!audioRef.current && typeof Audio !== "undefined") audioRef.current = new Audio();
  const timerRef = useRef(null);
  const stateRef = useRef({});

  useEffect(() => {
    api.get("/journeys").then((r) => setJourneys(r.data.journeys));
    api.get("/objects").then((r) => setObjects(r.data.objects));
    api.get("/narrator/voices").then((r) => setVoices(r.data.voices)).catch(() => {});
  }, []);

  const goTo = useCallback((idx) => {
    setActive((a) => {
      if (a) {
        const clamped = Math.max(0, Math.min(a.stops.length - 1, idx));
        setStopIndex(clamped);
        setFocusToken((t) => t + 1);
      }
      return a;
    });
  }, []);

  const startJourney = async (id) => {
    const r = await api.get(`/journeys/${id}`);
    setActive(r.data.journey);
    setStopIndex(0);
    setFocusToken((t) => t + 1);
    setPlaying(true);
  };

  const advance = useCallback(() => {
    const s = stateRef.current;
    if (!s.active) return;
    if (s.stopIndex < s.active.stops.length - 1) goTo(s.stopIndex + 1);
    else setPlaying(false);
  }, [goTo]);

  // keep latest state for audio/timer callbacks
  stateRef.current = { active, stopIndex, playing, muted };

  // audio end -> advance (voice-driven pacing)
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnded = () => { if (stateRef.current.playing) advance(); };
    a.addEventListener("ended", onEnded);
    a.addEventListener("error", onEnded);
    return () => { a.removeEventListener("ended", onEnded); a.removeEventListener("error", onEnded); };
  }, [advance]);

  const currentStop = active?.stops[stopIndex];
  const currentObj = currentStop?.object;
  const realm = currentObj?.realm || "solar";

  // fetch + play narration when the stop / voice changes
  useEffect(() => {
    const a = audioRef.current;
    if (!active || !currentStop || !a) return;
    a.pause();
    if (muted) return;
    let cancelled = false;
    setNarrating(true);
    api.post("/narrate", { text: currentStop.narration, voice })
      .then((r) => {
        if (cancelled) return;
        a.src = `data:audio/mp3;base64,${r.data.audio}`;
        if (stateRef.current.playing) a.play().catch(() => {});
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setNarrating(false); });
    return () => { cancelled = true; };
  }, [active, stopIndex, voice, muted]); // eslint-disable-line

  // play / pause the audio
  useEffect(() => {
    const a = audioRef.current;
    if (!a || muted) return;
    if (playing) { if (a.src) a.play().catch(() => {}); }
    else a.pause();
  }, [playing, muted]);

  // fallback timer (muted, or audio unavailable) to keep the tour moving
  useEffect(() => {
    if (!active || !playing) return;
    const useTimer = muted;
    if (!useTimer) return;
    timerRef.current = setTimeout(advance, 9000);
    return () => clearTimeout(timerRef.current);
  }, [active, playing, stopIndex, muted, advance]);

  // safety fallback if narration never plays
  useEffect(() => {
    if (!active || !playing || muted) return;
    const t = setTimeout(() => { if (stateRef.current.playing) advance(); }, 22000);
    return () => clearTimeout(t);
  }, [active, playing, stopIndex, muted, advance]);

  const exit = () => {
    setActive(null);
    setPlaying(false);
    if (audioRef.current) audioRef.current.pause();
  };

  const pickVoice = (id) => {
    setVoice(id);
    localStorage.setItem(VOICE_KEY, id);
    setVoiceMenu(false);
  };

  if (!active) {
    return (
      <div className="relative min-h-screen" data-testid="demo-select">
        <TopNav />
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-28 pb-20">
          <div className="flex items-center gap-2 mb-4">
            <Path size={18} weight="duotone" color="#00D8FF" />
            <span className="text-tel text-[10px] text-cyan-400/80">Investor Demo Mode</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-tight max-w-2xl">Cinematic guided journeys.</h1>
          <p className="text-white/55 mt-5 max-w-xl">Curated storytelling sequences spanning the solar system and the wider galaxy — press play, choose a narrator, and let the camera fly.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-14">
            {journeys.map((j, i) => (
              <button key={j.id} data-testid={`journey-${j.id}`} onClick={() => startJourney(j.id)}
                className="group text-left glass rounded-2xl p-7 hover:border-cyan-400/40 transition-colors fade-up" style={{ animationDelay: `${i * 90}ms` }}>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${j.accent}22`, boxShadow: `0 0 18px ${j.accent}44` }}>
                    <Play size={18} weight="fill" color={j.accent} />
                  </div>
                  <span className="text-tel text-[9px] text-white/40">{j.duration}</span>
                </div>
                <h3 className="font-display font-bold text-2xl tracking-tight mt-5" style={{ color: j.accent }}>{j.title}</h3>
                <p className="text-white/50 text-sm mt-1">{j.subtitle}</p>
                <p className="text-white/45 text-sm mt-4 leading-relaxed">{j.description}</p>
                <span className="inline-flex items-center gap-1.5 mt-6 text-sm text-white/70 group-hover:text-cyan-300 transition-colors">Launch journey <CaretRight size={14} weight="bold" /></span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 vignette" data-testid="demo-play">
      <div className="absolute inset-0 z-0" key={realm}>
        {realm === "solar" && objects.length > 0 && (
          <SolarSystem objects={objects} selectedId={currentObj?.id} focusToken={focusToken}
            onSelect={noop} onDeepDive={noop} showLabels={false} showOrbits paused />
        )}
        {realm === "galaxy" && (
          <GalaxyMap selectedId={currentObj?.id} focusToken={focusToken} onSelect={noop} onDeepDive={noop} onReturnSolar={noop} />
        )}
      </div>

      <div className="fixed top-0 left-0 right-0 z-20 h-16 flex items-center justify-between px-6" style={{ background: "linear-gradient(180deg, rgba(3,3,5,0.8), transparent)" }}>
        <div className="flex items-center gap-3">
          <span className="text-tel text-[10px]" style={{ color: active.accent }}>{active.title}</span>
          <span className="text-tel text-[9px] text-white/35">Stop {stopIndex + 1} / {active.stops.length}</span>
        </div>
        <button data-testid="demo-exit" onClick={exit} className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"><X size={18} /> Exit</button>
      </div>

      <div className="fixed top-16 left-0 right-0 z-20 px-6 flex gap-1.5">
        {active.stops.map((_, i) => (
          <div key={i} className="h-0.5 flex-1 rounded-full overflow-hidden bg-white/10">
            <div className="h-full transition-all duration-500" style={{ width: i <= stopIndex ? "100%" : "0%", background: active.accent }} />
          </div>
        ))}
      </div>

      {/* Info card (right) */}
      <div key={`card-${stopIndex}`} data-testid="demo-info-card" className="fixed right-6 top-24 z-20 w-72 glass rounded-2xl overflow-hidden slide-in-right hidden sm:block">
        {currentObj?.image && <img src={currentObj.image} alt={currentObj.name} className="w-full h-32 object-cover" />}
        <div className="p-5">
          <div className="text-tel text-[9px] text-cyan-400/80">{currentObj?.type}</div>
          <h3 className="font-display font-bold text-xl tracking-tight mt-1">{currentObj?.name}</h3>
          <div className="mt-4 space-y-2">
            {Object.entries(currentObj?.facts || {}).slice(0, 4).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 text-xs">
                <span className="text-white/40">{k}</span>
                <span className="font-mono-tel text-white/85 text-right">{v}</span>
              </div>
            ))}
          </div>
          <button data-testid="demo-deepdive" onClick={() => setDeepDiveObj(currentObj)}
            className="w-full mt-5 flex items-center justify-center gap-2 py-2.5 rounded-full text-xs font-semibold bg-cyan-400/15 text-cyan-200 hover:bg-cyan-400/25 transition-colors">
            <BookOpen size={14} weight="fill" /> Astrophysical Analysis
          </button>
        </div>
      </div>

      {/* Narration */}
      <div className="fixed bottom-0 left-0 right-0 z-20 p-6 sm:p-10" style={{ background: "linear-gradient(0deg, rgba(3,3,5,0.9), transparent)" }}>
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: currentObj?.color, boxShadow: `0 0 12px ${currentObj?.color}` }} />
            <span className="text-tel text-[10px] text-white/50">{currentObj?.name}</span>
            {!muted && narrating && (
              <span data-testid="narrating-indicator" className="flex items-center gap-1.5 text-tel text-[9px] text-cyan-400/80">
                <MicrophoneStage size={11} weight="fill" /> narrating…
              </span>
            )}
          </div>
          <p key={stopIndex} className="font-display font-light text-2xl sm:text-3xl tracking-tight leading-snug text-white fade-up">{currentStop.narration}</p>

          <div className="flex items-center justify-between mt-8">
            <div className="flex items-center gap-3">
              <button data-testid="demo-prev" onClick={() => goTo(stopIndex - 1)} disabled={stopIndex === 0}
                className="p-3 rounded-full border border-white/15 text-white/70 hover:text-white disabled:opacity-30 transition-colors"><CaretLeft size={18} weight="bold" /></button>
              <button data-testid="demo-playpause" onClick={() => setPlaying((p) => !p)}
                className="p-4 rounded-full bg-cyan-400 text-[#030305] glow-cyan hover:bg-cyan-300 transition-colors">
                {playing ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
              </button>
              <button data-testid="demo-next" onClick={() => goTo(stopIndex + 1)} disabled={stopIndex === active.stops.length - 1}
                className="p-3 rounded-full border border-white/15 text-white/70 hover:text-white disabled:opacity-30 transition-colors"><CaretRight size={18} weight="bold" /></button>
            </div>

            {/* Narrator controls */}
            <div className="relative flex items-center gap-2">
              <button data-testid="demo-mute" onClick={() => setMuted((m) => !m)}
                className={`p-3 rounded-full border transition-colors ${muted ? "border-white/15 text-white/60 hover:text-white" : "border-cyan-400/50 text-cyan-200"}`}
                title={muted ? "Unmute narration" : "Mute narration"}>
                {muted ? <SpeakerSlash size={18} weight="duotone" /> : <SpeakerHigh size={18} weight="duotone" />}
              </button>
              <button data-testid="demo-voice-toggle" onClick={() => setVoiceMenu((v) => !v)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white/80 hover:border-cyan-400/50 hover:text-cyan-200 transition-colors text-sm">
                <MicrophoneStage size={16} weight="fill" /> {voices.find((v) => v.id === voice)?.label || "Narrator"}
              </button>
              {voiceMenu && (
                <div data-testid="demo-voice-menu" className="absolute bottom-full right-0 mb-3 w-64 glass rounded-2xl p-2 space-y-1">
                  {voices.map((v) => (
                    <button key={v.id} data-testid={`voice-${v.id}`} onClick={() => pickVoice(v.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-xl transition-colors ${voice === v.id ? "bg-cyan-400/15" : "hover:bg-white/5"}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white/90">{v.label}</span>
                        {voice === v.id && <Check size={15} weight="bold" className="text-cyan-400" />}
                      </div>
                      <div className="text-white/45 text-xs mt-0.5">{v.desc}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {deepDiveObj && <DeepDive object={deepDiveObj} onClose={() => setDeepDiveObj(null)} />}
    </div>
  );
}
